import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';

export const TRANSACTION_STATUS = {
  successful: 'successful',
  failed: 'failed',
  pending: 'pending',
};

export const TRANSACTION_ICON_TYPES = {
  party: 'party',
  family: 'family',
  swaps: 'swaps',
  boost: 'boost',
  rapcoin: 'rapcoin',
  corporate: 'corporate',
};

const DEFAULT_PAGE_LIMIT = 10;

export const TRANSACTION_IAP_TYPES = {
  currency: 'currency',
  xp: 'xp',
  rapcoin: 'rapcoin',
  gems: 'gems',
  rooms: 'rooms',
};

export const WALLET_TAB_IAP_TYPE = {
  xps: TRANSACTION_IAP_TYPES.xp,
  rapcoins: TRANSACTION_IAP_TYPES.rapcoin,
  rapgems: TRANSACTION_IAP_TYPES.gems,
  viproom: TRANSACTION_IAP_TYPES.rooms,
};

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') return String(value).trim();
  }
  return fallback;
}

function pickNumber(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function normalizeStatus(value = '') {
  const status = String(value).trim().toLowerCase();

  if (['success', 'successful', 'completed', 'paid'].includes(status)) {
    return TRANSACTION_STATUS.successful;
  }
  if (['fail', 'failed', 'failure', 'cancelled', 'canceled'].includes(status)) {
    return TRANSACTION_STATUS.failed;
  }
  if (['pending', 'processing', 'in_progress', 'in-progress', 'initiated'].includes(status)) {
    return TRANSACTION_STATUS.pending;
  }

  return TRANSACTION_STATUS.pending;
}

function resolveIconUrl(item = {}) {
  const metadata = item?.metadata ?? {};

  return pickString(item, ['icon_url', 'iconUrl', 'iconURL', 'icon'], '') ||
    pickString(metadata, ['icon_url', 'iconUrl', 'iconURL', 'icon'], '');
}

function resolveIconType(item = {}) {
  const metadata = item?.metadata ?? {};
  const packageType = pickString(metadata, ['packageType'], '').toLowerCase();
  const packageName = pickString(metadata, ['packageName'], '').toLowerCase();
  const haystack = `${packageType} ${packageName}`;

  if (packageType === 'rapcoinbundle' || haystack.includes('rapcoin')) {
    return TRANSACTION_ICON_TYPES.rapcoin;
  }
  if (haystack.includes('swap') || haystack.includes('flip')) {
    return TRANSACTION_ICON_TYPES.swaps;
  }
  if (haystack.includes('boost')) return TRANSACTION_ICON_TYPES.boost;

  const rooms = metadata?.iapPackage?.rooms;
  if (Array.isArray(rooms) && rooms.length > 0) {
    const roomType = String(rooms[0]?.type ?? '').toLowerCase();
    if (roomType.includes('family')) return TRANSACTION_ICON_TYPES.family;
    if (roomType.includes('corporate')) return TRANSACTION_ICON_TYPES.corporate;
    if (roomType.includes('kitty') || roomType.includes('party')) {
      return TRANSACTION_ICON_TYPES.party;
    }
  }

  if (packageType === 'subscriptionpackage') return TRANSACTION_ICON_TYPES.family;
  if (packageType === 'gems') return TRANSACTION_ICON_TYPES.boost;
  if (packageType === 'pass') return TRANSACTION_ICON_TYPES.boost;

  return TRANSACTION_ICON_TYPES.rapcoin;
}

function formatTransactionDateTime(value, { includeOnPrefix = false } = {}) {
  if (value == null || value === '') return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).trim();
  }

  const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(' ', ' ');

  const dateLabel = `: ${month} ${day}, ${year}  at ${time}`;
  return includeOnPrefix ? `on ${dateLabel}` : dateLabel;
}

function resolveSubtitle(item = {}) {
  const metadata = item?.metadata ?? {};
  const description = pickString(metadata, ['description'], '');
  const purchasedAt =
    item?.created_at ?? item?.createdAt ?? item?.purchasedAt ?? item?.transactionDate ?? item?.date;

  if (description) {
    const dateTimeLabel = formatTransactionDateTime(purchasedAt);
    return dateTimeLabel ? `${description}${dateTimeLabel}` : description;
  }

  return formatTransactionDateTime(purchasedAt, { includeOnPrefix: true });
}

function formatAmountLabel(item = {}) {
  const amount = pickNumber(item, ['value', 'amount', 'price', 'paidAmount']);
  const currency = pickString(item, ['currency', 'amountCurrency'], 'INR').toUpperCase();
  const txnType = pickString(item, ['type', 'direction', 'amountDirection', 'transactionType'], '').toLowerCase();
  const isCredit =
    txnType === 'credit' ||
    item?.isCredit === true ||
    item?.credit === true;

  if (amount == null) return '';

  const prefix = isCredit ? '+' : '-';

  if (currency === 'INR' || currency === 'RUPEES' || item?.showRupee !== false) {
    return `${prefix}₹${Math.abs(amount)}`;
  }

  return `${prefix}${Math.abs(amount)}`;
}

function resolveTitle(item = {}) {
  const metadata = item?.metadata ?? {};
  const packageName = pickString(metadata, ['packageName'], '');
  if (packageName) return packageName;

  return pickString(item, ['title', 'productName', 'name', 'itemName', 'label'], 'Transaction');
}

function resolveInvoiceUrl(item = {}) {
  const invoice = item?.invoice;
  if (!invoice || typeof invoice !== 'object') {
    return pickString(item, ['invoiceUrl', 'invoiceLink', 'receiptUrl'], '');
  }

  return pickString(invoice, ['url', 'invoiceUrl', 'receiptUrl', 'downloadUrl'], '');
}

function resolveInvoiceState(item, status, invoiceUrl) {
  if (status !== TRANSACTION_STATUS.successful) {
    return {
      showInvoiceButton: false,
      canDownloadInvoice: false,
      invoiceMessage: '',
    };
  }

  const invoice = item?.invoice;
  const invoiceStatus =
    invoice && typeof invoice === 'object'
      ? pickString(invoice, ['status'], '').toLowerCase()
      : '';
  const canDownload =
    invoiceStatus === 'generated' || (invoiceStatus !== 'not_generated' && Boolean(invoiceUrl));
  const invoiceMessage =
    invoice && typeof invoice === 'object'
      ? pickString(invoice, ['message'], '')
      : '';

  return {
    showInvoiceButton: true,
    canDownloadInvoice: canDownload,
    invoiceMessage: invoiceMessage || 'Invoice will be generated shortly',
  };
}

function mapTransactionItem(item, index) {
  const status = normalizeStatus(pickString(item, ['status', 'paymentStatus', 'transactionStatus'], ''));
  const invoiceUrl = resolveInvoiceUrl(item);
  const invoiceState = resolveInvoiceState(item, status, invoiceUrl);

  return {
    id: item?._id ?? item?.id ?? item?.transactionId ?? `transaction-${index}`,
    title: resolveTitle(item),
    status,
    amountLabel: formatAmountLabel(item),
    subtitle: resolveSubtitle(item),
    iconType: resolveIconType(item),
    iconUrl: resolveIconUrl(item),
    invoiceUrl,
    showInvoiceButton: invoiceState.showInvoiceButton,
    canDownloadInvoice: invoiceState.canDownloadInvoice,
    invoiceMessage: invoiceState.invoiceMessage,
    raw: item,
  };
}

function extractTransactionsPayload(body) {
  const data = body?.data ?? body;
  const transactions = Array.isArray(data?.transactions)
    ? data.transactions
    : Array.isArray(data?.iaps)
      ? data.iaps
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data)
            ? data
            : [];

  const pagination = data?.pagination ?? {
    page: 1,
    limit: DEFAULT_PAGE_LIMIT,
    total: transactions.length,
    pages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };

  return { transactions, pagination };
}

export async function fetchTransactions({
  page = 1,
  limit = DEFAULT_PAGE_LIMIT,
  type = TRANSACTION_IAP_TYPES.currency,
} = {}) {
  const response = await axios_adapter.get(ENDPOINTS.USER_IAPS_TRANSACTIONS, {
    params: { type, page, limit },
  });
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load transactions.');
  }

  const { transactions, pagination } = extractTransactionsPayload(body);

  return {
    items: transactions.map(mapTransactionItem),
    pagination,
  };
}

export function downloadTransactionInvoice(transaction) {
  const url =
    transaction?.invoiceUrl ||
    transaction?.raw?.invoice?.url ||
    transaction?.raw?.invoiceUrl ||
    transaction?.raw?.receiptUrl;

  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }

  return false;
}
